// capacitor-env.js — the ONE place that answers "WHICH runtime is this code
// running in?" — today: the Capacitor Android/iOS shell (a real device WebView)
// or an ordinary web browser tab. Both the offline outbox store (outbox.js → getOutbox) and the
// SWR read cache (medplum-live.js) branch on this to pick their on-device
// storage backend (native SQLite/SQLCipher) vs the web backend
// (IndexedDB/localStorage). Kept tiny and dependency-free so the web bundle can
// import it without pulling in any Capacitor plugin code.
//
// Detection is Capacitor's own runtime signal: the shell injects a global
// `Capacitor` object whose `isNativePlatform()` returns true only inside the
// native WebView. On the web there is no such global (or it reports 'web'), so
// this returns false and every consumer keeps its existing web behaviour
// byte-for-byte. There is deliberately NO config flag or env var — the platform
// is a fact of where the code is running, not a setting.

/**
 * True only when running inside the native Capacitor shell (Android/iOS
 * WebView). False in any ordinary web browser / SSR / test (jsdom) context.
 * @returns {boolean}
 */
export function isCapacitorNative() {
  return getRuntimePlatform() === 'capacitor';
}

/**
 * The runtime this code is executing in, as ONE value instead of a chain of
 * boolean probes. Today it answers 'capacitor' | 'web'.
 *
 * WHY a string and not a second boolean: the replatform adds a THIRD runtime
 * (Expo / React Native, EXPO_REPLATFORM_PLAN.md Phase 2), and a per-runtime
 * boolean per call site scales badly — `isCapacitorNative() ? a : b` silently
 * routes Expo down the *web* branch, which is wrong for storage, networking and
 * audio alike. A platform NAME makes each seam switch explicitly and makes an
 * unhandled platform visible. Adding 'expo' is then one branch here (React
 * Native has no `document`; `navigator.product === 'ReactNative'`), and every
 * consumer that must care fails to match rather than defaulting to web.
 *
 * Detection stays the platform's own runtime signal, never a config flag: the
 * Capacitor shell injects a global `Capacitor` object whose `isNativePlatform()`
 * returns true only inside the native WebView.
 *
 * @returns {'capacitor' | 'web'}
 */
export function getRuntimePlatform() {
  try {
    const cap = (typeof window !== 'undefined' && window.Capacitor) || null;
    if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) return 'capacitor';
  } catch (_) {
    // A throwing/exotic `window.Capacitor` means "not the Capacitor shell".
  }
  return 'web';
}
